import React, { useEffect, useRef } from 'react';
import type LeafletNamespace from 'leaflet';
import { useInView } from '../hooks/useInView';
import { settingNotes } from '../data/property';
type LeafletApi = typeof LeafletNamespace;
// 37°50'24.4"S 144°59'35.8"E
const PROPERTY_LAT = -37.84011;
const PROPERTY_LNG = 144.99328;
const PROPERTY_LABEL = '14 William Street, South Yarra VIC 3141';
type Amenity = {
  name: string;
  lat: number;
  lng: number;
};
const AMENITIES: Amenity[] = [{
  name: 'Toorak Road',
  lat: -37.8394,
  lng: 144.9942
}, {
  name: 'Chapel Street',
  lat: -37.843,
  lng: 144.995
}, {
  name: 'South Yarra Station',
  lat: -37.8384,
  lng: 144.9925
}, {
  name: 'Melbourne Girls Grammar',
  lat: -37.8324,
  lng: 144.9862
}, {
  name: 'Melbourne Grammar',
  lat: -37.8348,
  lng: 144.976
}, {
  name: 'Christ Church Grammar',
  lat: -37.8393,
  lng: 144.9855
}, {
  name: 'Melbourne High',
  lat: -37.83556,
  lng: 144.99444
}];
export function WilliamStreetMap() {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<LeafletNamespace.Map | null>(null);
  const {
    ref: sectionRef,
    inView
  } = useInView<HTMLElement>({
    rootMargin: '400px',
    once: true
  });
  useEffect(() => {
    if (!inView || mapInstanceRef.current) return;
    let cancelled = false;
    let resizeHandler: (() => void) | null = null;
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    import('leaflet').then(mod => {
      const L: LeafletApi = mod.default ?? mod as unknown as LeafletApi;
      if (cancelled || !mapRef.current || mapInstanceRef.current) return;
      const isTouch = L.Browser.mobile;
      const map = L.map(mapRef.current, {
        center: [PROPERTY_LAT, PROPERTY_LNG],
        zoom: isTouch ? 14 : 15,
        maxZoom: 16,
        zoomControl: false,
        attributionControl: false,
        scrollWheelZoom: false,
        dragging: !isTouch,
        tap: false
      });
      L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}', {
        maxZoom: 16
      }).addTo(map);
      L.control.zoom({
        position: 'bottomright'
      }).addTo(map);
      const propertyIcon = L.divIcon({
        className: 'property-marker',
        html: `<div style="width:34px;height:34px;background:#000;border-radius:50%;border:2px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/>
            <path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
          </svg>
        </div>`,
        iconSize: [34, 34],
        iconAnchor: [17, 17],
        popupAnchor: [0, -17]
      });
      L.marker([PROPERTY_LAT, PROPERTY_LNG], {
        icon: propertyIcon
      }).addTo(map).bindPopup(`<div style="font-family: 'Newsreader', serif; text-align: center; padding: 4px 0;">
            <strong style="font-size: 13px; letter-spacing: 0.05em;">${PROPERTY_LABEL}</strong>
          </div>`, {
        closeButton: false,
        className: 'property-popup'
      });
      // 10-minute walking radius (~800m at 80m/min)
      L.circle([PROPERTY_LAT, PROPERTY_LNG], {
        radius: 800,
        color: '#8FC6EC',
        weight: 1.5,
        opacity: 0.9,
        fillColor: '#C6E4FA',
        fillOpacity: 0.3,
        dashArray: '6, 6',
        interactive: false
      }).addTo(map);
      const walkLabelIcon = L.divIcon({
        className: 'walk-label',
        html: `<span style="font-family:'Newsreader',serif;font-size:9px;letter-spacing:0.15em;text-transform:uppercase;color:rgba(0,0,0,0.3);white-space:nowrap;background:rgba(255,255,255,0.85);padding:2px 6px;">10 min walk</span>`,
        iconSize: [0, 0],
        iconAnchor: [0, 0]
      });
      L.marker([PROPERTY_LAT + 800 / 111320, PROPERTY_LNG], {
        icon: walkLabelIcon,
        interactive: false
      }).addTo(map);
      const amenityIcon = L.divIcon({
        className: 'amenity-marker',
        html: `<div style="width:12px;height:12px;background:#000;border-radius:50%;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.25);"></div>`,
        iconSize: [12, 12],
        iconAnchor: [6, 6]
      });
      AMENITIES.forEach(poi => {
        L.marker([poi.lat, poi.lng], {
          icon: amenityIcon
        }).addTo(map).bindPopup(`<div style="font-family: 'Newsreader', serif; text-align: center; padding: 2px 0;">
              <span style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em; color: #737373;">${poi.name}</span>
            </div>`, {
          closeButton: false
        });
      });
      mapInstanceRef.current = map;
      resizeHandler = () => map.invalidateSize();
      window.addEventListener('resize', resizeHandler);
    });
    return () => {
      cancelled = true;
      if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler);
        resizeHandler = null;
      }
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [inView]);
  return <section ref={sectionRef} id="location" className="relative bg-white border-b border-black/5" data-id="element-80" data-mp="2i626ev6kucgx">
      <div className="px-6 md:px-12 pt-20 md:pt-32 pb-10 md:pb-16" data-id="element-81" data-mp="2n43q9ibtuftg">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-24" data-id="element-82" data-mp="287mckkg60p3q">
          <div className="md:col-span-5" data-id="element-83" data-mp="289yw321kn7iw">
            <div className="flex items-center gap-4 mb-5 md:mb-8" data-id="element-84" data-mp="2d5nv949oimeo">
              <span className="w-8 h-px bg-black" aria-hidden="true" data-id="element-85" data-mp="1u1u73ljtamrq"></span>
              <span className="eyebrow text-black" data-id="element-86" data-mp="1u46uie8c9pc6">The Setting</span>
            </div>
            <h2 className="font-display text-[2.25rem] md:text-5xl text-black leading-[1.08]" data-id="element-87" data-mp="1pa2qo3uddxfb">
              Metres from <br data-id="element-88" data-mp="1ka6ma3hrg3z8" />
              <span className="italic text-gray-muted" data-id="element-89" data-mp="1fd7uvspheb4q">Toorak Road.</span>
            </h2>
          </div>
          <div className="md:col-span-7 flex items-end" data-id="element-90" data-mp="2d0ommwr3nr8y">
            <ul className="w-full flex flex-col border-t border-black/10 md:border-t-0 md:pl-12 md:border-l" data-id="element-91" data-mp="2d06ti8zqht59">
              {settingNotes.map(note => <li key={note} className="font-body text-gray-dark text-base md:text-lg leading-relaxed py-4 border-b border-black/10 last:border-0" data-id="element-92" data-mp="23292jdqhpi8y">
                  {note}
                </li>)}
            </ul>
          </div>
        </div>
      </div>

      <div ref={mapRef} className="w-full h-[52svh] min-h-[340px] md:h-[65vh] md:min-h-[500px] bg-gray-light" data-id="element-93" data-mp="mnr38x9oqy9k" />

      <style dangerouslySetInnerHTML={{
      __html: `
        .leaflet-container { font-family: 'Newsreader', serif; }
        .leaflet-control-zoom {
          border: 1px solid rgba(0,0,0,0.1) !important;
          border-radius: 0 !important;
          box-shadow: none !important;
        }
        .leaflet-control-zoom a {
          color: #000 !important;
          background: #fff !important;
          border-radius: 0 !important;
          border-bottom: 1px solid rgba(0,0,0,0.1) !important;
          width: 44px !important;
          height: 44px !important;
          line-height: 44px !important;
          font-size: 18px !important;
        }
        .leaflet-control-zoom a:last-child { border-bottom: none !important; }
        .leaflet-popup-content-wrapper {
          border-radius: 0 !important;
          box-shadow: 0 2px 12px rgba(0,0,0,0.12) !important;
          padding: 8px 12px !important;
        }
        .leaflet-popup-tip { box-shadow: none !important; }
        .leaflet-control-attribution { display: none !important; }
      `
    }} data-id="element-94" data-mp="2y9t0pbrf3q5x" />
    </section>;
}